import React, { useState } from 'react';
import { Badge, Card } from 'flowbite-react';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi';
import type { WorkflowStep, DecisionFrame } from '../../api/workflowInspector';
import { formatDuration, getStatusColor } from '../../api/workflowInspector';
import DecisionFrameDetail from './DecisionFrameDetail';

interface StepTimelineProps {
  steps: WorkflowStep[];
  decisionFrames: DecisionFrame[];
}

const dotColor: Record<string, string> = {
  completed: 'bg-green-500',
  failed: 'bg-red-500',
  running: 'bg-blue-500',
  pending: 'bg-gray-300',
};

const StepTimeline: React.FC<StepTimelineProps> = ({ steps, decisionFrames }) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  // Map decision frames by step name for lookup
  const framesByStep = new Map<string, DecisionFrame>();
  for (const frame of decisionFrames) {
    framesByStep.set(frame.Step_Name, frame);
  }

  if (steps.length === 0) {
    return (
      <Card>
        <h4 className="text-md font-semibold text-gray-900 mb-2">Execution Timeline</h4>
        <p className="text-sm text-gray-500">No steps recorded for this execution.</p>
      </Card>
    );
  }

  return (
    <Card>
      <h4 className="text-md font-semibold text-gray-900 mb-4">Execution Timeline</h4>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-4">
          {steps.map((step) => {
            const frame = framesByStep.get(step.Step_Name);
            const isExpanded = expandedStep === step.Step_ID;

            return (
              <div key={step.Step_ID} className="relative pl-9">
                {/* Dot */}
                <div
                  className={`absolute left-1.5 top-2 w-3 h-3 rounded-full ring-2 ring-white ${
                    dotColor[step.Status] || 'bg-gray-300'
                  }`}
                />

                {/* Step card */}
                <div
                  className={`border rounded-lg px-3 py-2 ${
                    isExpanded ? 'border-indigo-300 bg-indigo-50/30' : 'border-gray-200 bg-white'
                  }`}
                >
                  <button
                    onClick={() => setExpandedStep(isExpanded ? null : step.Step_ID)}
                    className="w-full text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {frame ? (
                          isExpanded ? (
                            <HiChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <HiChevronRight className="w-4 h-4 text-gray-400" />
                          )
                        ) : (
                          <div className="w-4" />
                        )}
                        <span className="text-sm font-medium text-gray-900">{step.Step_Name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge color={getStatusColor(step.Status)} size="xs">
                          {step.Status}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatDuration(step.Duration_Ms)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-1 ml-6">
                      <span className="text-xs text-gray-500">Agent: {step.Agent_ID}</span>
                      {step.Retry_Count > 0 && (
                        <Badge color="warning" size="xs">
                          {step.Retry_Count} retries
                        </Badge>
                      )}
                      {frame && frame.Constraints_Violated.length > 0 && (
                        <Badge color="failure" size="xs">
                          {frame.Constraints_Violated.length} violation{frame.Constraints_Violated.length !== 1 ? 's' : ''}
                        </Badge>
                      )}
                      {frame && frame.Failover_Attempted && (
                        <Badge color="warning" size="xs">Failover</Badge>
                      )}
                      {frame && frame.Confidence !== null && (
                        <span className="text-xs text-gray-400">
                          {Math.round(frame.Confidence * 100)}% confidence
                        </span>
                      )}
                    </div>

                    {step.Error_Details && (
                      <p className="text-xs text-red-600 mt-1 ml-6 truncate">{step.Error_Details}</p>
                    )}
                  </button>

                  {/* Expanded decision frame */}
                  {isExpanded && frame && (
                    <DecisionFrameDetail frame={frame} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default StepTimeline;
